import {
  type CSSProperties,
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { token } from '@atlaskit/tokens';
import { setGlobalTheme } from '@atlaskit/tokens/set-global-theme';

import ConsentForm from '../../Flow/ConsentForm';
import {
  ReactConsentContext,
  useInfo,
  useToken,
  useSelectedOrg,
  useSelectedWorkspace,
} from '../../Flow/ConsentContextProvider';
import TWG from './TWG';
import TwgAdminRail from './TwgAdminRail';
import TwgFooter from './TwgFooter';
import { HIDDEN_TWG_SCOPES } from './PermissionsBullets/scope-catalog';

type ThemeVars = CSSProperties & Record<`--twg-${string}`, string>;

const themeVars: ThemeVars = {
  '--twg-page-bg': token('elevation.surface', '#ffffff'),
  '--twg-rail-bg': token('elevation.surface', '#ffffff'),
  '--twg-pane-bg': token('elevation.surface', '#ffffff'),
  '--twg-divider': token('color.border', '#dcdfe4'),
  '--twg-heading': token('color.text', '#172b4d'),
  '--twg-text': token('color.text', '#172b4d'),
  '--twg-muted': token('color.text.subtle', '#626f86'),
  '--twg-card-bg': token('elevation.surface.raised', '#ffffff'),
  '--twg-card-text': token('color.text', '#172b4d'),
  '--twg-card-muted': token('color.text.subtle', '#44546f'),
  '--twg-card-border': token('color.border', '#dcdfe4'),
  '--twg-card-hover': token('color.background.neutral.subtle.hovered', '#f7f8f9'),
  '--twg-illustration-bg': token('color.background.neutral.bold', '#101214'),
  '--twg-info-panel-bg': token('color.background.neutral', '#ffffff'),
  '--twg-info-panel-text': token('color.text', '#172b4d'),
  '--twg-info-panel-muted': token('color.text.subtle', '#172b4d'),
  '--twg-warning-bg': token('color.background.warning', '#fff7d6'),
  '--twg-warning-text': token('color.text', '#172b4d'),
  '--twg-warning-icon': token('color.icon.warning', '#e56910'),
  '--twg-info-icon': token('color.icon.information', '#bf63f3'),
  '--twg-empty-icon': token('color.border', '#dcdfe4'),
  '--twg-link': token('color.link', '#0c66e4'),
};

const styles: Record<string, CSSProperties> = {
  page: {
    backgroundColor: 'var(--twg-page-bg)',
    boxSizing: 'border-box',
    color: 'var(--twg-text)',
    minHeight: 'calc(100vh - 48px)',
    minWidth: 0,
    width: '100%',
  },
  shell: {
    backgroundColor: 'var(--twg-pane-bg)',
    boxSizing: 'border-box',
    display: 'flex',
    marginInline: 'auto',
    maxWidth: '1356px',
    minHeight: 'calc(100vh - 48px)',
    minWidth: 0,
    overflow: 'hidden',
    paddingBottom: '88px',
    position: 'relative',
    width: '100%',
  },
  shellNarrow: {
    flexDirection: 'column',
    overflow: 'visible',
    paddingBottom: 0,
  },
  leftRail: {
    backgroundColor: 'var(--twg-rail-bg)',
    borderRightColor: 'var(--twg-divider)',
    borderRightStyle: 'solid',
    borderRightWidth: '1px',
    boxSizing: 'border-box',
    flex: '0 0 440px',
    minHeight: 'calc(100vh - 136px)',
    paddingBlock: '64px',
    paddingInline: '64px',
  },
  leftRailNarrow: {
    borderBottomColor: 'var(--twg-divider)',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderRightWidth: 0,
    flex: '0 0 auto',
    minHeight: 0,
    paddingBlock: '64px 40px',
    paddingInline: '16px',
    width: '100%',
  },
  rightPane: {
    backgroundColor: 'var(--twg-pane-bg)',
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    paddingBlock: '80px',
    paddingInline: '64px 80px',
    position: 'relative',
    width: '100%',
  },
  rightPaneNarrow: {
    paddingBlock: '24px 32px',
    paddingInline: '16px',
  },
  content: {
    maxWidth: '772px',
    width: '100%',
  },
  contentNarrow: {
    maxWidth: 'none',
  },
};

const narrowViewportQuery = '(max-width: 767px)';
interface TwgConsentLayoutProps {
  defaultPermissionsEditMode?: boolean;
}

const useIsNarrowViewport = () => {
  const getInitialValue = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(narrowViewportQuery).matches
      : false;
  const [isNarrow, setIsNarrow] = useState(getInitialValue);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(narrowViewportQuery);
    const onChange = () => setIsNarrow(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return isNarrow;
};

const TwgConsentLayout: FunctionComponent<TwgConsentLayoutProps> = ({
  defaultPermissionsEditMode = false,
}) => {
  const consentContext = useContext(ReactConsentContext);
  const { scopes } = useToken();
  const { disabledScopes, filteredScopes } = useInfo();
  const { selectedOrg, isOrgLoading, orgError } = useSelectedOrg();
  const { selectedWorkspace } = useSelectedWorkspace();
  const isNarrow = useIsNarrowViewport();
  const [permissionsEditMode, setPermissionsEditMode] = useState(
    defaultPermissionsEditMode,
  );

  const acceptDisabled =
    !!consentContext?.acceptDisabled ||
    !selectedOrg ||
    !selectedWorkspace ||
    isOrgLoading ||
    !!orgError;
  const selectedScopes = consentContext?.selectedScopes ?? [];
  const requestedScopes = useMemo(() => {
    return new Set(
      [...scopes, ...(filteredScopes ?? []), ...(disabledScopes ?? [])].filter(
        (scope) => !HIDDEN_TWG_SCOPES.has(scope),
      ),
    );
  }, [disabledScopes, filteredScopes, scopes]);
  const allowedScopes = useMemo(() => {
    return new Set(
      (filteredScopes ?? scopes).filter((scope) => !HIDDEN_TWG_SCOPES.has(scope)),
    );
  }, [filteredScopes, scopes]);
  const selectedScopeCount = selectedScopes.filter((scope) =>
    allowedScopes.has(scope),
  ).length;
  const selectedPermissionsSummary =
    permissionsEditMode &&
    selectedWorkspace &&
    !isOrgLoading &&
    !orgError &&
    requestedScopes.size > 0
      ? {
          selectedCount: selectedScopeCount,
          totalCount: requestedScopes.size,
        }
      : undefined;

  useEffect(() => {
    setGlobalTheme({
      colorMode: 'auto',
      light: 'light',
      dark: 'dark',
    }).catch((e) =>
      console.warn('Theme failed to load', e),
    );
  }, []);

  return (
    <div style={{ ...themeVars, ...styles.page }}>
      <ConsentForm>
        <div
          style={{
            ...styles.shell,
            ...(isNarrow ? styles.shellNarrow : {}),
          }}
        >
          <aside
            style={{
              ...styles.leftRail,
              ...(isNarrow ? styles.leftRailNarrow : {}),
            }}
          >
            <TwgAdminRail />
          </aside>
          <section
            style={{
              ...styles.rightPane,
              ...(isNarrow ? styles.rightPaneNarrow : {}),
            }}
          >
            <div
              style={{
                ...styles.content,
                ...(isNarrow ? styles.contentNarrow : {}),
              }}
            >
              <TWG
                defaultPermissionsEditMode={defaultPermissionsEditMode}
                permissionsEditMode={permissionsEditMode}
                onPermissionsEditModeChange={setPermissionsEditMode}
              />
            </div>
          </section>
          <TwgFooter
            acceptDisabled={acceptDisabled}
            selectedPermissionsSummary={selectedPermissionsSummary}
          />
        </div>
      </ConsentForm>
    </div>
  );
};

export default TwgConsentLayout;
